<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-aria/slider';
import {HeaderInfo, FunctionAPI, TypeContext, InterfaceType, TypeLink} from '@react-spectrum/docs';
import packageData from '@react-aria/slider/package.json';
import statelyDocs from 'docs:@react-stately/slider';
import i18nDocs from 'docs:@react-aria/i18n';

```jsx import
import {useSlider, useSliderThumb} from '@react-aria/slider';
```

---
category: Forms
keywords: [slider, range, aria]
---

# useSlider

<p>{docs.exports.useSlider.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['useSlider', 'useSliderThumb']}
  sourceData={[
    {type: 'W3C', url: 'https://www.w3.org/TR/wai-aria-1.2/#slider'}
  ]} />


## API

<FunctionAPI function={docs.exports.useSlider} links={docs.links} />
<FunctionAPI function={docs.exports.useSliderThumb} links={docs.links} />

## Features

The [&lt;input type="range"&gt;](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/range)
HTML element can be used to build a slider, however it is
very difficult to style cross browser. `useSlider` and `useSliderThumb` help achieve accessible
sliders that can be styled as needed.


* Exposed to assistive technology as a `group` and `slider` via ARIA
* Labeling support for accessibility
* Internationalized number formatting as a percentage or value
* Multiple thumbs, or group of range values
* Horizontal and Vertical layouts
* Normalized across devices

## Anatomy

Sliders consist of a track element showing the range of available values,
a thumb or multiple thumbs showing the current value(s), and a label.
Thumbs consist of an indicator that designates a position on the track.
The track element represents the value(s) visually, the thumbs represent individual
values with `role=slider`. The group of value(s) is represented by the wrapping element
with `role=group`.

### useSlider hook
`useSlider` returns three sets of props that you should spread onto the appropriate element:

<TypeContext.Provider value={docs.links}>
  <InterfaceType properties={docs.links[docs.exports.useSlider.return.id].properties} />
</TypeContext.Provider>

If there is no visual label, an `aria-label` or `aria-labelledby` prop must be passed instead
to identify the element to screen readers.

### useSliderThumb hook
`useSliderThumb` returns three sets of props that you should spread onto the appropriate element:

<TypeContext.Provider value={docs.links}>
  <InterfaceType properties={docs.links[docs.exports.useSliderThumb.return.id].properties} />
</TypeContext.Provider>

If there is no visual label, an `aria-label` or `aria-labelledby` prop must be passed instead
to identify the element to screen readers.

Slider thumb value(s) are managed by <TypeLink links={statelyDocs.links} type={statelyDocs.exports.useSliderState} />.

## Example

### Single Thumb
```css
.slider {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 300px;
}

.sliderLabel {
  display: flex;
  align-self: stretch;
}

.label {
  color: #8b8b8b;
}

.value {
  color: #585858;
  margin-inline-start: auto;
}

.thumb {
  position: absolute;
  top: 4px;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.thumbHandle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #707070;
  box-sizing: border-box;
  background-color: #f5f5f5;
  box-shadow: 0 0 0 4px #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumbFocusVisible .thumbHandle {
  box-shadow: 0 0 0 2px #f5f5f5, 0 0 0 4px #2c83eb;
}

.thumbFocused {
  border-color: #2c83eb;
}

.thumbDisabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.thumbDisabled label {
  cursor: inherit;
}

.rail {
  position: absolute;
  background-color: #e4e4e4;
  height: 3px;
  top: 13px;
  width: 100%;
}

.filledRail {
  position: absolute;
  background-color: #707070;
  height: 3px;
  top: 13px;
  inset-inline-start: 0;
}

.track {
  position: relative;
  height: 30px;
  width: 100%;
}

.trackContainer {
  position: relative;
  width: 100%;
}
```


```tsx example
import {useSliderState} from '@react-stately/slider';
import {FocusRing} from '@react-aria/focus';
import {VisuallyHidden} from '@react-aria/visually-hidden';
import {I18nProvider} from '@react-aria/i18n';

function Slider(props) {
  let trackRef = React.useRef(null);
  let inputRef = React.useRef(null);
  let origin = props.origin ?? props.minValue ?? 0;

  let multiProps = {
    ...props,
    value: props.value == null ? undefined :  [props.value],
    defaultValue: props.defaultValue == null ? undefined : [props.defaultValue],
    onChange: props.onChange == null ? undefined : (vals) => props.onChange(vals[0]),
    onChangeEnd: props.onChangeEnd == null ? undefined : (vals) => props.onChangeEnd(vals[0])
  };

  let state = useSliderState(multiProps);
  let {
    containerProps,
    trackProps,
    labelProps
  } = useSlider(multiProps, state, trackRef);

  let {thumbProps, inputProps} = useSliderThumb({
    index: 0,
    isDisabled: props.isDisabled,
    trackRef,
    inputRef
  }, state);

  let value = state.values[0];

  return (
    <I18nProvider locale="en-US">
      <div className="slider" {...containerProps}>
        <div className="sliderLabel">
          {props.label && <label {...labelProps} className="label">{props.label}</label>}
          <div className="value">{state.getThumbValueLabel(0)}</div>
        </div>
        <div className="trackContainer">
          {
            // We make rail, filledRail, and track siblings in the DOM, so that trackRef has no children.
            // User must click on the trackRef to drag by track, and so it comes last in the DOM.
          }
          <div className="rail" />
          <div
            className="filledRail"
            style={{
              insetInlineStart: `${state.getValuePercent(Math.min(value, origin)) * 100}%`,
              width: `${(state.getValuePercent(Math.max(value, origin)) - state.getValuePercent(Math.min(value, origin))) * 100}%`
            }} />
          <div ref={trackRef} className="track" {...trackProps} />
          <FocusRing within focusRingClass="thumbFocusVisible" focusClass="thumbFocused">
            <div
              className="thumb"
              style={{
                insetInlineStart: `${state.getThumbPercent(0) * 100}%`
              }}>
              <div {...thumbProps} className="thumbHandle">
                <VisuallyHidden><input className="input" ref={inputRef} {...inputProps} /></VisuallyHidden>
              </div>
            </div>
          </FocusRing>
        </div>
      </div>
    </I18nProvider>
  );
}

<Slider
  label="Opacity"
  formatOptions={{style: 'percent'}}
  maxValue={1}
  step={0.01}
  onChange={(e) => console.log('onChange', e)}
  onChangeEnd={(e) => console.log('onChangeEnd', e)} />
```

## Internationalization

### Value formatting

Formatting the value that should be displayed in the value label or `aria-valuetext`
is handled by <TypeLink links={statelyDocs.links} type={statelyDocs.exports.useSliderState} />.
The formatting can be controlled using the `formatOptions` prop.
For this to work, the <TypeLink links={i18nDocs.links} type={i18nDocs.exports.I18nProvider} />
must be somewhere in the hierarchy above the Slider.
This will tell the formatter what locale to use.

### RTL

In right-to-left languages, the slider should be mirrored. The label is right-aligned,
the value is left-aligned. Ensure that your CSS accounts for this.
